import { useState, useEffect, useCallback } from "react";
import { BillDataEntity } from "../../domain/entities/BillEntity";
import { billsAPI, Bill } from "../../../../services/api";

interface UseBillDetailsReturn {
  billDocument: Bill | null;
  billJson: BillDataEntity | null;
  signedUrl: string | null;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseBillDetailsOptions {
  initialBillJson?: BillDataEntity | null;
  initialBillDocument?: Bill | null;
  initialSignedUrl?: string | null;
  initialFileName?: string | null;
}

/**
 * Custom hook to fetch bill details
 * Fetches from API by default
 * Only skips fetch if skipFetch flag is explicitly set to true
 * This prevents unnecessary refetches when navigating back from vendor/customer pages
 */
export const useBillDetails = (
  documentId: string | null,
  options?: UseBillDetailsOptions
): UseBillDetailsReturn => {
  const [billDocument, setBillDocument] = useState<Bill | null>(
    options?.initialBillDocument || null
  );
  const [billJson, setBillJson] = useState<BillDataEntity | null>(
    options?.initialBillJson || null
  );
  const [signedUrl, setSignedUrl] = useState<string | null>(
    options?.initialSignedUrl || null
  );
  const [fileName, setFileName] = useState<string | null>(
    options?.initialFileName || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBillDetails = useCallback(async () => {
    if (!documentId) {
      console.error("No documentId to fetch bill details");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log("Fetching bill details for documentId:", documentId);
      const billData = await billsAPI.getBill(documentId);

      // Update bill document state
      setBillDocument(billData);

      // Update signed URL and filename
      if (billData.signed_url) {
        setSignedUrl(billData.signed_url);
      }
      if (billData.original_filename) {
        setFileName(billData.original_filename);
      }

      // Update bill JSON if available
      if (billData.bill_json) {
        setBillJson(billData.bill_json);
      }

      console.log("Bill details fetched successfully:", billData);
    } catch (err) {
      console.error("Error fetching bill details:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch bill details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  // Fetch on mount unless returning from vendor/customer page
  useEffect(() => {
    // Check if we're returning from vendor/customer page (autoSelectVendor in sessionStorage)

    // if (options?.initialBillJson) {
    //   setBillJson(options.initialBillJson);
    //   return;
    // }
    // Otherwise, always fetch fresh data
    fetchBillDetails();
  }, [fetchBillDetails, options?.initialBillJson]);

  return {
    billDocument,
    billJson,
    signedUrl,
    fileName,
    loading,
    error,
    refetch: fetchBillDetails,
  };
};
